import { Bucket } from "../../enum/jobEnums";
import { CandidateReviewPayload, ReviewCriteria } from "../../types/candidate";
import { CandidateAssessedVia, ReviewCriteriaKey, TalentAssessmentEnum } from "../../enum/reviewEnums";
import { AddProfessionalReviewCandidate, AddTalentReviewCandidate } from "../../types/addReviewCandidate";

const mapAssessmentMethod = (method: TalentAssessmentEnum): CandidateAssessedVia => {
  switch (method) {
    case TalentAssessmentEnum.VIDEOS:
      return CandidateAssessedVia.VIDEO_ANALYSIS;
    case TalentAssessmentEnum.TRYOUT:
      return CandidateAssessedVia.TRYOUT_INTERVIEW;
    default:
      return CandidateAssessedVia.TRYOUT_INTERVIEW;
  }
};

export const formatTalentReviewPayload = (data: AddTalentReviewCandidate, bucket: Bucket): CandidateReviewPayload => {
  const criteria: ReviewCriteria[] = [
    // Technical Skills
    { key: ReviewCriteriaKey.FIRST_TOUCH_RECEIVING, ...data.firstTouchReceiving },
    { key: ReviewCriteriaKey.PASSING_RANGE_ACCURACY, ...data.passingRangeAccuracy },
    { key: ReviewCriteriaKey.DRIBBLING_1V1, ...data.dribbling1v1Ability },
    { key: ReviewCriteriaKey.SHOOTING_FINISHING, ...data.shootingFinishing },
    { key: ReviewCriteriaKey.CROSSING_DELIVERY, ...data.crossingDelivery },
    { key: ReviewCriteriaKey.HEADING_TECHNIQUE, ...data.headingTechnique },
    // Tactical Awareness
    { key: ReviewCriteriaKey.POSITIONING, ...data.positioning },
    { key: ReviewCriteriaKey.OFF_BALL_MOVEMENT, ...data.offTheBallMovement },
    { key: ReviewCriteriaKey.DEFENSIVE_AWARENESS, ...data.defensiveAwareness },
    { key: ReviewCriteriaKey.TRANSITION_PLAY, ...data.transitionPlay },
    { key: ReviewCriteriaKey.SPATIAL_AWARENESS, ...data.spatialAwareness },
    { key: ReviewCriteriaKey.TACTICAL_ADAPTABILITY, ...data.tacticalAdaptability },
    // Athleticism & Physical
    { key: ReviewCriteriaKey.ACCELERATION, ...data.acceleration },
    { key: ReviewCriteriaKey.TOP_SPEED, ...data.topSpeed },
    { key: ReviewCriteriaKey.STRENGTH, ...data.strength },
    { key: ReviewCriteriaKey.ENDURANCE, ...data.endurance },
    { key: ReviewCriteriaKey.AGILITY, ...data.agility },
    { key: ReviewCriteriaKey.FLEXIBILITY_COORDINATION, ...data.flexibilityCoordination },
    // Mindset & Psychological
    { key: ReviewCriteriaKey.CONCENTRATION, ...data.concentration },
    { key: ReviewCriteriaKey.CONFIDENCE, ...data.confidence },
    { key: ReviewCriteriaKey.LEADERSHIP, ...data.leadership },
    { key: ReviewCriteriaKey.DISCIPLINE, ...data.discipline },
    { key: ReviewCriteriaKey.WORK_RATE, ...data.workRate },
    { key: ReviewCriteriaKey.RESILIENCE, ...data.resilience },
    { key: ReviewCriteriaKey.COACHABILITY, ...data.coachAbility },
    { key: ReviewCriteriaKey.PROFESSIONALISM, ...data.professionalism },
  ];

  return {
    bucket,
    review: {
      overallFitScore: data.overallFitScore,
      assessedVia: mapAssessmentMethod(data.talentAssessment),
      criteria,
      ginoSummary: data.ginoSummary,
      tags: data.tags,
      finalNarrative: null, // Default as per example
    },
  };
};

export const formatProfessionalReviewPayload = (
  data: AddProfessionalReviewCandidate,
  bucket: Bucket
): CandidateReviewPayload => {
  const criteria: ReviewCriteria[] = [
    // Professional Expertise
    { key: ReviewCriteriaKey.DOMAIN_KNOWLEDGE, ...data.domainKnowledge },
    { key: ReviewCriteriaKey.TRACK_RECORD, ...data.trackRecordAchievements },
    { key: ReviewCriteriaKey.ANALYTICAL_SKILLS, ...data.analyticalSkills },
    { key: ReviewCriteriaKey.TALENT_IDENTIFICATION, ...data.talentIdentification },
    { key: ReviewCriteriaKey.INNOVATION_METHODS, ...data.innovationMethods },
    // Communication & Leadership
    { key: ReviewCriteriaKey.LEADERSHIP, ...data.leadership },
    { key: ReviewCriteriaKey.COMMUNICATION_CLARITY, ...data.communicationClarity },
    { key: ReviewCriteriaKey.TEAM_MANAGEMENT, ...data.teamManagement },
    { key: ReviewCriteriaKey.INFLUENCE_NETWORKING, ...data.influenceNetworking },
    { key: ReviewCriteriaKey.CULTURAL_ADAPTABILITY, ...data.culturalAdaptability },
    // Professionalism & Work Ethic
    { key: ReviewCriteriaKey.DISCIPLINE, ...data.discipline },
    { key: ReviewCriteriaKey.WORK_RATE, ...data.workRate },
    { key: ReviewCriteriaKey.INTEGRITY_ETHICS, ...data.integrityEthics },
    { key: ReviewCriteriaKey.RESILIENCE, ...data.resilience },
    { key: ReviewCriteriaKey.COACHABILITY, ...data.coachAbility },
    { key: ReviewCriteriaKey.PROFESSIONAL_PRESENCE, ...data.professionalPresence },
    // Development & Growth
    { key: ReviewCriteriaKey.LEARNING_ORIENTATION, ...data.learningOrientation },
    { key: ReviewCriteriaKey.ADAPTABILITY, ...data.adaptability },
    { key: ReviewCriteriaKey.FUTURE_POTENTIAL, ...data.futurePotential },
    { key: ReviewCriteriaKey.SPECIALIZATION_FIT, ...data.specializationFit },
  ];

  return {
    bucket,
    review: {
      overallFitScore: data.overallFitScore,
      assessedVia: mapAssessmentMethod(data.talentAssessment),
      criteria,
      ginoSummary: data.ginoSummary,
      tags: data.tags,
      finalNarrative: null,
    },
  };
};
